import React, { useId, useState } from 'react';
import { Platform, Pressable, type PressableProps, type StyleProp, type View, type ViewStyle } from 'react-native';

import { Box, Text, useTheme } from 'app/shopify/theme';
import { INTERACTION } from 'app/shopify/generated/interaction';

export type RadioButtonSize = 'small' | 'large';
export type RadioButtonState = 'enabled' | 'hover' | 'pressed' | 'focus' | 'disabled';

export interface RadioButtonProps extends Omit<PressableProps, 'children' | 'style' | 'onPress'> {
  label?: string;
  description?: string;
  showDescription?: boolean;
  /** Hide the visible label while retaining it as the accessible name. */
  showText?: boolean;
  selected?: boolean;
  size?: RadioButtonSize;
  /** Preview a visual state; enabled controls still respond to input. */
  state?: RadioButtonState;
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
  ref?: React.Ref<View>;
  /** Used by RadioButtonList for web arrow-key navigation. */
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
}

export function RadioButton({
  label = 'Radio label',
  description = 'Description',
  showDescription = false,
  showText = true,
  selected = false,
  size = 'small',
  state = 'enabled',
  disabled = false,
  onPress,
  onFocus,
  onBlur,
  onHoverIn,
  onHoverOut,
  onKeyDown,
  accessibilityLabel,
  accessibilityHint,
  accessibilityState,
  style,
  ref,
  ...pressableProps
}: RadioButtonProps) {
  const theme = useTheme();
  const descriptionId = useId();
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [keyboardPressed, setKeyboardPressed] = useState(false);
  const isDisabled = disabled || state === 'disabled';
  const controlSize = Number(theme.tokens.sizing['sizing.general.x6']);
  // The Figma dot is half the control diameter; it has no separate size binding.
  const dotSize = controlSize / 2;
  const focusOutset = theme.spacing['spacing.general.x1'];
  const hasDescription = showText && showDescription && Boolean(description);
  const labelSize = Number(theme.tokens.typography[`typography.font-size.body.${size === 'large' ? 'large' : 'base'}`]);
  const descriptionSize = Number(theme.tokens.typography['typography.font-size.body.small']);

  return (
    <Pressable
      {...pressableProps}
      {...(Platform.OS === 'web'
        ? {
            onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
              onKeyDown?.(event);
              // React Native Web's Pressable only handles Space for button roles.
              if (
                !event.defaultPrevented &&
                !isDisabled &&
                !event.altKey &&
                !event.ctrlKey &&
                !event.metaKey &&
                (event.key === ' ' || event.key === 'Spacebar')
              ) {
                event.preventDefault();
                setKeyboardPressed(true);
                if (!event.repeat && !selected) onPress?.();
              }
            },
            onKeyUp: (event: React.KeyboardEvent<HTMLElement>) => {
              if (event.key === ' ' || event.key === 'Spacebar') setKeyboardPressed(false);
            },
            'aria-describedby': hasDescription ? descriptionId : undefined,
          }
        : {})}
      ref={ref}
      accessibilityRole="radio"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityHint={accessibilityHint ?? (hasDescription ? description : undefined)}
      accessibilityState={{ ...accessibilityState, checked: selected, disabled: isDisabled }}
      disabled={isDisabled}
      hitSlop={pressableProps.hitSlop ?? theme.spacing['spacing.general.x3']}
      onPress={() => {
        if (!isDisabled && !selected) onPress?.();
      }}
      onFocus={(event) => {
        setFocused(true);
        onFocus?.(event);
      }}
      onBlur={(event) => {
        setFocused(false);
        setKeyboardPressed(false);
        onBlur?.(event);
      }}
      onHoverIn={(event) => {
        setHovered(true);
        onHoverIn?.(event);
      }}
      onHoverOut={(event) => {
        setHovered(false);
        onHoverOut?.(event);
      }}
      style={[{ alignSelf: 'flex-start', maxWidth: '100%' }, Platform.OS === 'web' && { outlineWidth: 0 }, style]}
    >
      {({ pressed }) => {
        const showPressed =
          !isDisabled && (state === 'pressed' || (state === 'enabled' && (pressed || keyboardPressed)));
        const showHover = !isDisabled && (state === 'hover' || (state === 'enabled' && hovered));
        const showFocus = !isDisabled && (state === 'focus' || focused);
        const borderWidth =
          theme.borderWidths[selected || showHover || showPressed ? 'border-width.standard' : 'border-width.subtle'];
        const borderColor = isDisabled
          ? 'border.disabled'
          : selected
            ? 'border.selected'
            : showHover || showPressed
              ? 'border.interactive'
              : 'border.primary';
        const layerColor = showPressed ? 'state-layer.press-brand' : showHover ? 'state-layer.hover-brand' : undefined;

        return (
          <Box flexDirection="row" gap="spacing.general.x3" alignItems="flex-start">
            <Box width={controlSize} height={controlSize} flexShrink={0} accessible={false}>
              <Box
                width={controlSize}
                height={controlSize}
                borderRadius="radius.full"
                alignItems="center"
                justifyContent="center"
                borderWidth={borderWidth}
                borderColor={borderColor}
                backgroundColor={isDisabled && !showText ? 'surface.disabled' : 'surface.default'}
              >
                {selected ? (
                  <Box
                    width={dotSize}
                    height={dotSize}
                    borderRadius="radius.full"
                    backgroundColor={isDisabled ? 'surface.disabled' : 'surface.interactive'}
                  />
                ) : null}
              </Box>
              {layerColor ? (
                <Box
                  pointerEvents="none"
                  position="absolute"
                  top={0}
                  bottom={0}
                  left={0}
                  right={0}
                  borderRadius="radius.full"
                  backgroundColor={layerColor}
                />
              ) : null}
              {showFocus ? (
                <Box
                  pointerEvents="none"
                  position="absolute"
                  top={-focusOutset}
                  bottom={-focusOutset}
                  left={-focusOutset}
                  right={-focusOutset}
                  borderRadius="radius.full"
                  borderWidth={theme.borderWidths['border-width.emphasized']}
                  borderColor="border.focused"
                />
              ) : null}
            </Box>
            {showText ? (
              <Box minHeight={controlSize} minWidth={0} flexShrink={1} justifyContent="center" gap="spacing.general.x1">
                <Text
                  variant={size === 'large' ? 'bodyLargeMedium' : 'bodyBase'}
                  color={isDisabled ? 'text.disabled' : 'text.primary'}
                  style={{ lineHeight: labelSize * INTERACTION.labelLineHeight }}
                >
                  {label}
                </Text>
                {hasDescription ? (
                  <Text
                    nativeID={descriptionId}
                    variant="bodySmall"
                    color={isDisabled ? 'text.disabled' : 'text.secondary'}
                    style={{ lineHeight: descriptionSize * INTERACTION.labelLineHeight }}
                  >
                    {description}
                  </Text>
                ) : null}
              </Box>
            ) : null}
          </Box>
        );
      }}
    </Pressable>
  );
}
